import { Button } from '@parrot-software/design-system';
import { LoaderCircle, Mail } from '@parrot-software/design-system/icons';
import { Trans, useTranslation } from 'react-i18next';

import { StepCard } from '@identity/presentation/components/base';
import { useRequestPasswordRecovery } from '@identity/presentation/hooks';
import { translateIdentityError } from '@identity/presentation/utils';

export type RecoverySentViewProps = {
  /** The address the request was made with: what the user just typed. */
  email: string;
  /** Returns to the capture view, with the typed address still in the field. */
  onUseAnotherEmail: () => void;
};

/**
 * The notice that follows an accepted request.
 *
 * It waits for nothing and it polls for nothing. There is no credential to ask with, and any signal
 * that changed here — even a spinner that stops — would reveal that the address exists and is
 * verified, which is exactly what the identical answer protects. The link is opened in another tab
 * or on another device anyway, and the reset continues there on its own.
 *
 * The wording never claims the mail was sent, because the answer does not say whether the account
 * exists. It names both conditions and the thirty minutes, which is the only place the front end can
 * warn that leaving the inbox for later loses the link, and reveals nothing: the deadline is the same
 * whether or not the account exists.
 *
 * The address is shown in full. The user typed it seconds ago, so hiding characters protects nothing
 * and takes away what they need to spot a typo — which matters more here than anywhere else, because
 * a typo and an unregistered address look identical: an empty inbox.
 *
 * The way back to the capture view sits next to the resend, because the person who needs it is
 * looking at the wrong address right above; below the fold it would only nudge them to resend to the
 * same wrong inbox.
 *
 * @param {RecoverySentViewProps} props - Component properties
 */
export const RecoverySentView = ({ email, onUseAnotherEmail }: RecoverySentViewProps) => {
  const { t } = useTranslation('identity');
  const { errorKey, errorRetryAfterSeconds, isSending, request } = useRequestPasswordRecovery();

  const resend = () => request({ email, isResend: true });

  return (
    <StepCard
      description={
        <Trans components={{ strong: <strong /> }} i18nKey="identity:recovery.sent.body" values={{ email }} />
      }
      icon={
        <span className="flex size-8 items-center justify-center rounded-full bg-bg-info">
          <Mail aria-hidden className="size-4 text-info" />
        </span>
      }
      title={t('recovery.sent.title')}
    >
      <div className="flex flex-col gap-6 font-sans">
        <p className="text-sm font-bold text-muted-foreground" data-testid="recovery-sent-close-window">
          {t('recovery.sent.closeWindow')}
        </p>

        {errorKey && (
          <p className="text-xs text-destructive" data-testid="recovery-resend-error">
            {translateIdentityError(t, errorKey, errorRetryAfterSeconds)}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
          <Button className="w-full sm:w-auto sm:px-[15px]" disabled={isSending} onClick={resend} variant="outline">
            {isSending && (
              <LoaderCircle
                aria-hidden
                className="size-4 shrink-0 animate-spin"
                data-testid="recovery-resend-spinner"
              />
            )}
            {t('recovery.sent.resend')}
          </Button>

          <Button className="w-full sm:w-auto sm:px-[15px]" onClick={onUseAnotherEmail} variant="link">
            {t('recovery.sent.useAnotherEmail')}
          </Button>
        </div>
      </div>
    </StepCard>
  );
};
